/**
 * Cross-cutting helpers shared by pages, models and components.
 *
 * Four groups live here: route constants plus the allowlist backing the auth
 * gate, name/text normalisation used to match people across sources, date and
 * URL formatting for display, and the image-proxy URL builder.
 *
 * Note the sibling `src/utils/` directory - `viewUtils.ts` there holds the
 * render-only helpers.
 */
import axios from 'axios';
import geoData from '../config/google-trends-geo.json';

import {
  Car,
  Shirt,
  BarChart2,
  Film,
  Utensils,
  Gamepad2,
  HeartPulse,
  Paintbrush,
  GraduationCap,
  Scale,
  HelpCircle,
  Dog,
  Gavel,
  FlaskConical,
  ShoppingCart,
  Volleyball,
  Laptop,
  Plane,
  Leaf,
  List,
} from 'lucide-react';

import { LucideIcon } from 'lucide-react';

/** Viewport bucket resolved once at boot and stored in the initial state. */
export type SCREEN_CLASS = 'WIDE' | 'MEDIUM' | 'NARROW';

// Routes that must stay reachable without a session; see `isUrlMatchingPrefix`.
export const LOGIN_PATH = '/user/login';
export const REGISTER_PATH = '/user/register';
export const RESET_PASSWORD_PATH = '/user/reset-password';
export const VERIFY_EMAIL_PATH = '/user/verify-email';
export const INITIATE_PATH = '/user/initiate';
export const SHARE_PATH = '/share/';

export type Category = {
  id: string;
  name: string;
  icon: LucideIcon;
  color: string;
};

/**
 * Google Trends category taxonomy, with the icon and accent colour each one is
 * drawn with. The `id`s mirror the values the backend stores, so they are part
 * of the wire format - rename with care.
 */
export const categories = [
  { id: 'all', name: 'All', icon: List, color: '#6B7280' }, // Gray
  { id: 'autos_and_vehicles', name: 'Autos and Vehicles', icon: Car, color: '#EF4444' }, // Red
  { id: 'beauty_and_fashion', name: 'Beauty and Fashion', icon: Shirt, color: '#EC4899' }, // Pink
  { id: 'business_and_finance', name: 'Business and Finance', icon: BarChart2, color: '#0EA5E9' }, // Sky Blue
  { id: 'entertainment', name: 'Entertainment', icon: Film, color: '#F59E0B' }, // Amber
  { id: 'food_and_drink', name: 'Food and Drink', icon: Utensils, color: '#10B981' }, // Emerald Green
  { id: 'games', name: 'Games', icon: Gamepad2, color: '#8B5CF6' }, // Violet
  { id: 'health', name: 'Health', icon: HeartPulse, color: '#22C55E' }, // Green
  { id: 'hobbies_and_leisure', name: 'Hobbies and Leisure', icon: Paintbrush, color: '#14B8A6' }, // Teal
  { id: 'jobs_and_education', name: 'Jobs and Education', icon: GraduationCap, color: '#3B82F6' }, // Blue
  { id: 'law_and_government', name: 'Law and Government', icon: Scale, color: '#F97316' }, // Orange
  { id: 'other', name: 'Other', icon: HelpCircle, color: '#9CA3AF' }, // Cool Gray
  { id: 'pets_and_animals', name: 'Pets and Animals', icon: Dog, color: '#F87171' }, // Rose Red
  { id: 'politics', name: 'Politics', icon: Gavel, color: '#7C3AED' }, // Indigo
  { id: 'science', name: 'Science', icon: FlaskConical, color: '#4ADE80' }, // Light Green
  { id: 'shopping', name: 'Shopping', icon: ShoppingCart, color: '#E11D48' }, // Rose
  { id: 'sports', name: 'Sports', icon: Volleyball, color: '#2563EB' }, // Deep Orange
  { id: 'technology', name: 'Technology', icon: Laptop, color: '#6366F1' }, // Indigo/Violet
  {
    id: 'travel_and_transportation',
    name: 'Travel and Transportation',
    icon: Plane,
    color: '#0F766E',
  }, // Dark Teal
  { id: 'climate', name: 'Climate', icon: Leaf, color: '#16A34A' }, // Forest Green
];

/** Resolve a Google Trends geo id (e.g. `FR`) to its display name. */
export function getCountryNameById(id: string): string {
  const match = geoData.find((item) => item.geo_id === id);
  return match ? match.geo_description : id; // fallback: echo the raw geo_id back
}

/**
 * Fold a person's name down to a comparable key by dropping case, whitespace
 * and punctuation, so "J. R. R. Tolkien" and "jrr tolkien" collapse together.
 */
export function normalizeName(name: string) {
  return name.toLowerCase().replace(/[\s'".,!?]/g, '');
}

/**
 * Split a label such as `"World Health Organization (WHO)"` into its parts.
 *
 * A bare all-caps token is treated as an abbreviation with no known full name;
 * anything else is treated as a full name with no abbreviation.
 */
export function extractFullNameAndAbbreviation(text: string) {
  const regex = /^(.+?)\s*\((\b[A-Z]{2,}\b)\)$/; // matches "Full Name (ABBR)"
  const match = text.match(regex);

  let fullName: string | undefined = undefined;
  let abbreviation: string | undefined = undefined;

  if (match) {
    fullName = match[1].trim();
    abbreviation = match[2];
  } else if (/^\b[A-Z]{2,}\b$/.test(text)) {
    abbreviation = text;
  } else {
    fullName = text;
  }
  return { fullName, abbreviation };
}

export function isSingleWord(text: string) {
  const regex = /^[^\s]+$/; // Checks that the text contains no spaces
  return regex.test(text); // Returns true if text is a single word, false otherwise
}

export function isSingleWordWithUppercaseFirstLetter(text: string) {
  const regex = /^[A-Z][^\s]*$/; // Ensures the first letter is uppercase and there are no spaces
  return regex.test(text); // Returns true if text is a single word with an uppercase first letter, false otherwise
}

/**
 * Levenshtein edit distance, iterative full-matrix variant.
 *
 * Inlined rather than pulled from a package because the only caller compares
 * two short person names; O(n*m) is irrelevant at that size.
 */
export function levenshtein(a: string, b: string) {
  const tmp = [];
  let i,
    j,
    alen = a.length,
    blen = b.length,
    d,
    min;

  if (alen === 0) return blen;
  if (blen === 0) return alen;

  for (i = 0; i <= alen; i++) tmp[i] = [i];
  for (j = 0; j <= blen; j++) tmp[0][j] = j;

  for (i = 1; i <= alen; i++) {
    for (j = 1; j <= blen; j++) {
      min = tmp[i - 1][j] + 1;
      d = a[i - 1] === b[j - 1] ? 0 : 1;
      if (tmp[i][j - 1] + 1 < min) min = tmp[i][j - 1] + 1;
      if (tmp[i - 1][j - 1] + d < min) min = tmp[i - 1][j - 1] + d;
      tmp[i][j] = min;
    }
  }
  return tmp[alen][blen];
}

/**
 * Edit distance between two names after normalisation - the lower the number,
 * the more likely the two labels denote the same person. Callers pick their own
 * threshold.
 */
export function getDistance(text: string, name: string) {
  const normName1 = normalizeName(text);
  const normName2 = normalizeName(name);
  const distance = levenshtein(normName1.toLowerCase(), normName2.toLowerCase());
  return distance;
}

/** Clip `text` to `maxLength` characters, appending an ellipsis when cut. */
export function truncateText(text: string, maxLength: number) {
  return text.length > maxLength ? `${text.slice(0, maxLength)} ...` : text;
}

/** Map a pixel width onto the three layout buckets the UI branches on. */
export function screenWidthClassifier(windowWidth: number): SCREEN_CLASS {
  if (windowWidth >= 800) {
    return 'WIDE';
  } else if (windowWidth >= 600) {
    return 'MEDIUM';
  }

  return 'NARROW';
}

/**
 * True when `url` belongs to a route that renders without a session.
 *
 * Prefix-based on purpose: `SHARE_PATH` has to cover `/share/c/:candidateId`.
 * This is the allowlist the auth gate in `src/app.tsx` consults.
 */
export function isUrlMatchingPrefix(url: string): boolean {
  const urls = [
    LOGIN_PATH,
    REGISTER_PATH,
    RESET_PASSWORD_PATH,
    VERIFY_EMAIL_PATH,
    INITIATE_PATH,
    SHARE_PATH,
  ];
  return urls.some((prefix) => url.startsWith(prefix));
}

/**
 * Exact-match sibling of `isUrlMatchingPrefix`, restricted to the auth screens
 * (share pages excluded). Used where an exact route, not a family, is meant.
 */
export function includeUrl(url: string): boolean {
  const urls = [LOGIN_PATH, REGISTER_PATH, RESET_PASSWORD_PATH, VERIFY_EMAIL_PATH, INITIATE_PATH];
  return urls.includes(url);
}

/**
 * Pull the API's `{ success, errorCode, errorMessage }` envelope out of a failed
 * request, for callers that opted out of the global handler with
 * `skipErrorHandler` and render the message themselves.
 *
 * Returns `undefined` for anything that is not an axios error carrying a body -
 * network failures, aborts, thrown non-errors.
 */
export function parserError(error: any) {
  if (axios.isAxiosError(error) && error.response && error.response.data) {
    const data = error.response.data as {
      success: boolean;
      errorCode?: number;
      errorMessage?: string;
    };
    const success = data.success;
    const errorCode = data.errorCode;
    const errorMessage = data.errorMessage;
    return { success, errorCode, errorMessage };
  }
  return undefined;
}

/**
 * Initials for an avatar placeholder.
 *
 * A parenthesised suffix wins outright, because for organisations the bracketed
 * abbreviation ("World Health Organization (WHO)" -> "WHO") is the recognisable
 * short form; otherwise fall back to the first letter of each word.
 */
export function getNameInitials(name: string) {
  if (!name || typeof name !== 'string') return '';

  const match = name.match(/\(([^)]+)\)/);

  if (match) {
    return match[1];
  }

  return name
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

/**
 * Relative age of a Mongo document, derived from its id.
 *
 * The first four bytes of an ObjectId are its creation timestamp, so no extra
 * `createdAt` round-trip is needed. Renders as `just now` / `12m` / `5h`, then
 * switches to an absolute `Mar 3` once past a day.
 */
export function formatObjectIdDate(objectId: string) {
  const timestamp = parseInt(objectId.substring(0, 8), 16); // leading 4 bytes = creation time
  const date = new Date(timestamp * 1000);
  const now = new Date();

  const diff = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diff / (1000 * 60));
  const diffHours = Math.floor(diff / (1000 * 60 * 60));

  if (diffMinutes < 1) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m`;
  } else if (diffHours < 24) {
    return `${diffHours}h`;
  }

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Absolute creation date of a Mongo document, again read off its ObjectId.
 * Unlike `formatObjectIdDate` this never goes relative and is day/month-first.
 */
export function showDateFromObjectIdDate(objectId: string) {
  const timestamp = parseInt(objectId.substring(0, 8), 16); // leading 4 bytes = creation time
  const date = new Date(timestamp * 1000);

  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: '2-digit' };
  return date.toLocaleDateString('fr-FR', options);
}

/**
 * Host of a URL with any `www.` prefix stripped, for display next to a link.
 * Never throws: malformed or missing input yields `'unknown-domain'`.
 */
export function getDomain(urlString?: string): string {
  try {
    if (urlString) {
      const url = new URL(urlString);
      let domain = url.host;

      if (domain.startsWith('www.')) {
        domain = domain.substring(4);
      }

      return domain;
    }
  } catch (error) {
    console.error('Invalid URL:', error);
  }

  // Callers render this straight into the UI, so fail soft rather than throw.
  return 'unknown-domain';
}

/** Naive registrable-domain guess: keep the last two labels. */
function getRootDomain(domain: string): string {
  const parts = domain.split('.');
  return parts.slice(-2).join('.');
}

/**
 * Favicon for a site, served through Google's resolver rather than the site's
 * own `/favicon.ico` - many of the referenced domains have none, or serve one
 * that fails CORS.
 */
export function getFaviconUrl(urlString: string, size: number = 48): string {
  const domain = getDomain(urlString);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const rootDomain = getRootDomain(domain);

  // const fallbackUrl = `https://${rootDomain}/favicon.ico`;
  // return fallbackUrl;
  return `https://www.google.com/s2/favicons?sz=${size}&domain=${domain}`;
}

// 2026-09-24: was a standalone img.* subdomain, which went down along with
// the old datacenter. Moved to a same-origin path so it rides the main
// domain's DNS/TLS instead of needing its own subdomain - nginx strips the
// /imgproxy prefix and proxies to willnorris/imageproxy on ax42 (see
// kredibility-rest-server's scripts/deploy/55_nginx.sh).
const IMAGE_PROXY_HOST = '/imgproxy';
// Inline sources are not remote URLs; the proxy would only answer
// "remote URL must have http or https scheme".
const NON_PROXYABLE_SCHEME = /^(data:|blob:)/i;
// The imageproxy backend (willnorris/imageproxy, Go) decodes only
// JPEG/PNG/GIF (+BMP/TIFF). These extensions would always come back as
// "image: unknown format", yet browsers render them natively - so link straight
// to the original and save a pointless proxy round-trip.
const NON_PROXYABLE_EXT = /\.(ico|svg|avif)($|[?#])/i;

/** Whether the image proxy can transform this source (if not, use it as-is). */
export function isImageProxyable(src: string): boolean {
  return !NON_PROXYABLE_SCHEME.test(src) && !NON_PROXYABLE_EXT.test(src);
}

/** Build the resized proxy URL, passing through sources the proxy cannot handle. */
export function buildImageProxyUrl(src: string, size: number): string {
  return isImageProxyable(src) ? `${IMAGE_PROXY_HOST}/${size}x,c/${src}` : src;
}

/**
 * Normalise the citation syntax the LLM emits into plain markdown links.
 *
 * The model is inconsistent about bracketing - it produces `[citation:3]`,
 * `[[citation:3]]` and half-closed mixtures of the two - so each family is
 * first collapsed to the single-bracket form and only then rewritten to
 * `[citation](3)`, which the renderer turns into a footnote chip.
 *
 * `people` references work the same way but carry a 24-char ObjectId, and the
 * pattern is deliberately strict about that width so ordinary prose in square
 * brackets is left alone.
 */
export function markdownParse(text: string): string {
  return (
    text
      .replace(/\[\[([cC])itation/g, '[citation')
      .replace(/[cC]itation:(\d+)]]/g, 'citation:$1]')
      .replace(/\[\[([cC]itation:\d+)]](?!])/g, `[$1]`)
      .replace(/\[[cC]itation:(\d+)]/g, '[citation]($1)')
      // Same collapse pass for people references.
      .replace(/\[\[([pP])eople/g, '[people')
      // Only a 24-char hex ObjectId counts as a reference.
      .replace(/[pP]eople:([a-f0-9]{24})]]/g, 'people:$1]')
      .replace(/\[\[([pP]eople:[a-f0-9]{24})]](?!])/g, `[$1]`)
      .replace(/\[[pP]eople:([a-f0-9]{24})]/g, '[people]($1)')
  );
}

// export function markdownParse(text: string): string {
//   return text
//     // citation pre-processing
//     .replace(/\[citation:(\d+)]/gi, '[citation]($1)')

//     // people pre-processing
//     .replace(/\[people:([a-f0-9]{24})]/gi, '[people]($1)')
// }

/**
 * Best available portrait for a person, in descending order of trust: the
 * curated avatar, then the one scraped from X/Twitter, then Wikipedia's.
 * `undefined` means the caller should fall back to initials.
 */
export function getInfluencerImage(influencer: DATA.Influencer): string | undefined {
  let src = influencer.avatar;
  if (!src) {
    if (influencer.twitter && influencer.twitter.avatar) {
      src = influencer.twitter.avatar;
    }
  }

  if (!src) {
    if (influencer.wiki && influencer.wiki.avatar) {
      src = influencer.wiki.avatar;
    }
  }

  return src;
}

/**
 * Render a date as `- Mar 3, 2026` for use as a trailing byline fragment.
 * Missing and unparsable input both collapse to an empty string, so the caller
 * can concatenate unconditionally.
 */
export function formatEnglishDate(dateInput?: string) {
  if (!dateInput) {
    return '';
  }
  try {
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) {
      return '';
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return '- ' + formatter.format(date);
  } catch (err) {
    return '';
  }
}
